import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { FiShoppingCart, FiPackage, FiDollarSign, FiAlertCircle, FiActivity } from "react-icons/fi";

const ProductDisplayComponent = ({
    darkMode,
    filteredProducts,
    getStatusIcon,
    getStatusColor,
    getConfidenceColor,
    getProgressBarColor,
    getSalesIndicator,
    getStockIndicator,
    getPriceIndicator,
    containerVariants,
    itemVariants,
    secondaryTextColor,
    cardBorder
}) => {
  return (
    <>
        <AnimatePresence mode="wait">
          <motion.div
            key="cards-view"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
          >
            {filteredProducts?.map((product, index) => {
              const salesIndicator = getSalesIndicator(product.sales);
              const stockIndicator = getStockIndicator(product.stock);
              const priceIndicator = getPriceIndicator(product.price);
              
              return (
                <motion.div
                  key={product.id}
                  variants={itemVariants}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                  className={`group ${darkMode ? "bg-gradient-to-br from-gray-800 to-gray-900" : "bg-gradient-to-br from-white to-gray-50"} ${cardBorder} rounded-xl overflow-hidden border hover:border-blue-500/50 transition-all duration-300 shadow-lg ${darkMode ? "shadow-black/20" : "shadow-gray-200/50"}`}
                >
                  {/* Status Indicator */}
                  <div className={`h-1 w-full ${product.status === "CRITICAL" ? "bg-red-500" : product.status === "OPPORTUNITY" ? "bg-blue-500" : "bg-green-500"}`} />
                  
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 ${darkMode ? "bg-gray-700" : "bg-gray-100"} rounded-lg`}>
                          {getStatusIcon(product.status)}
                        </div>
                        <div>
                          <h3 className="font-bold text-lg truncate">{product.name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(product.status)} border`}>
                              {product.status}
                            </span>
                            <span className={`text-xs ${getConfidenceColor(product.confidence)}`}>
                              {product.confidence}% confidence
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Metrics */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className={`${darkMode ? "bg-gray-700/50" : "bg-gray-100"} p-3 rounded-lg`}>
                        <div className="flex items-center gap-2 mb-1">
                          <FiShoppingCart className={salesIndicator.color} />
                          <span className={`text-xs ${secondaryTextColor}`}>Sales</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-bold">{product.sales}</span>
                          <span className={`text-xs ${salesIndicator.color}`}>{salesIndicator.label}</span>
                        </div>
                      </div>
                      
                      <div className={`${darkMode ? "bg-gray-700/50" : "bg-gray-100"} p-3 rounded-lg`}>
                        <div className="flex items-center gap-2 mb-1">
                          <FiPackage className={stockIndicator.color} />
                          <span className={`text-xs ${secondaryTextColor}`}>Stock</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-bold">{product.stock}</span>
                          <span className={`text-xs ${stockIndicator.color}`}>{stockIndicator.label}</span>
                        </div>
                      </div>
                      
                      <div className={`${darkMode ? "bg-gray-700/50" : "bg-gray-100"} p-3 rounded-lg`}>
                        <div className="flex items-center gap-2 mb-1">
                          <FiDollarSign className={priceIndicator.color} />
                          <span className={`text-xs ${secondaryTextColor}`}>Price</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-bold">{product.price}</span>
                          <span className={`text-xs ${priceIndicator.color}`}>{priceIndicator.label}</span>
                        </div>
                      </div>
                    </div>

                    {/* Analysis */}
                    <div className="space-y-3">
                      <div>
                        <div className={`flex items-center gap-2 ${secondaryTextColor} text-sm mb-1`}>
                          <FiAlertCircle /> Analysis
                        </div>
                        <p className={darkMode ? "text-gray-300 text-sm" : "text-gray-700 text-sm"}>{product.reason}</p>
                      </div>
                      
                      <div>
                        <div className={`flex items-center gap-2 ${secondaryTextColor} text-sm mb-1`}>
                          <FiActivity /> Recommended Action
                        </div>
                        <p className={darkMode ? "text-blue-300 font-medium text-sm" : "text-blue-600 font-medium text-sm"}>{product.action}</p>
                      </div>
                    </div>

                    {/* Confidence Meter */}
                    <div className="mt-6">
                      <div className="flex justify-between text-sm mb-2">
                        <span className={secondaryTextColor}>Rule Confidence</span>
                        <span className={getConfidenceColor(product.confidence)}>
                          {product.confidence}%
                        </span>
                      </div>
                      <div className={`h-2 ${darkMode ? "bg-gray-700" : "bg-gray-200"} rounded-full overflow-hidden`}>
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${product.confidence}%` }}
                          transition={{ duration: 1, delay: 0.5 }}
                          className={`h-full ${getProgressBarColor(product.confidence)}`}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </AnimatePresence>
    </>
  )
}

export default ProductDisplayComponent